/**
 * Kanban-Ansicht der Anfragen — Alternative zur Listenansicht (`AnfragenGruppenAnsicht`),
 * NICHT eine zweite Datenquelle. Die Spalten sind exakt dieselben drei abgeleiteten Gruppen
 * (`AnzeigeGruppe` aus `@maestro/anfrage-stand`), nur nebeneinander statt gestapelt — derselbe
 * `fasseFuerAnzeigeZusammen`-Aufruf wie die Liste liefert die Abschnitte.
 * Seit 12.10.2026 (Antoine) kommt auf dem Board eine vierte Spalte „Abgelaufen" dazu, abgeleitet
 * aus denselben Zeilen (`KanbanSpalte`, `boardAbschnitte`) — die Listenansicht bleibt dreiteilig.
 *
 * Bewusst KEIN rohes Statusfeld als Spaltenquelle: `inquiries.tsx` (Kommentar „P10.1") beschreibt,
 * warum die alte statusbasierte Filterung entfernt wurde — `inquiries.status='quoted'` behauptete
 * faelschlich „Angebot gesendet" ohne echten Versandnachweis, Ursache eines Doppelversand-Bugs.
 *
 * Bewusst KEIN Drag&Drop (Entscheidung 04.09.2026): Karten sind klickbar wie in der Liste, aber
 * nicht zwischen Spalten verschiebbar — reine alternative Darstellung derselben Daten, keine neue
 * Schreibaktion.
 *
 * ── Redesign 08.09.2026 (Mockup von Antoine freigegeben: „Ja sehr gut. Setze das um.") ─────────
 * Drei Spalten „Du bist dran" / „Kunde ist dran" / „Zugesagt" (statt „Entschieden"), volle
 * Bildschirmbreite auf Desktop, farbige Status-Pille als Spaltenkopf (weiterhin ein echtes `<h3>`
 * — der bestehende E2E-Test `apps/e2e/tests/anfragen-kanban.spec.ts` sucht genau diese Rolle),
 * Karten mit ausgeschriebener Firma/Person/E-Mail/Datum statt abgeschnittenem Text, „Abgesagt" als
 * eingeklappter Unterbereich INNERHALB der „Zugesagt"-Spalte statt einer eigenen vierten Spalte.
 * Mobile-Korrektur (08.09.2026, noch am selben Tag): Antoine hat sich umentschieden — auf
 * schmalen Bildschirmen NICHT stapeln, sondern beim URSPRUENGLICHEN Verhalten bleiben (Spalten
 * nebeneinander, horizontaler Scroll, feste Kartenbreite). Nur der Desktop-Breakpoint (`md:`)
 * bekommt das neue volle-Breite-Raster.
 *
 * Die dritte Spalte heisst hier „Zugesagt", nicht in `ANZEIGE_GRUPPEN_TITEL.entschieden`
 * (`@maestro/anfrage-stand`) selbst umbenannt: diese Ueberschrift speist AUCH die Listenansicht
 * (`AnfragenGruppenAnsicht`), und dort steht unveraendert eine gemischte Menge aus Gewinnen UND
 * Absagen — „Zugesagt" waere über abgesagten Zeilen falsch. Nur diese Datei ueberschreibt den
 * Anzeige-Text lokal (`KANBAN_SPALTEN_TITEL`), die Struktur/Reihenfolge/Zaehlung bleibt exakt die
 * der Regel.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gruppenUntertitel, type AktionSchluessel, type AnfrageStand, type AnzeigeGruppe, type StandAktion, type StandGruppe } from "@maestro/anfrage-stand";
import { initialen, type AnfrageZeile, type AnzeigeAbschnitt } from "./AnfragenGruppen";
import type { Inquiry } from "./types";
import { Card, EmptyState, Icon, StatusPill, ZEILEN_AKTION, ZeilenLink } from "./components";
import { datumPruefenNoetig } from "./DatumVergangenHinweis";
import { PILL_CLASSES, formatDate, money, timeAgo, type PillTone, type StatusMeta } from "./format";
import { boardSummeText } from "../pages/inquiries";
import { A_LA_CARTE_KLEIN } from "../lib/a-la-carte";
import { abrechnungPfad, angebotPfad } from "../lib/pfade";
import { HerkunftPille } from "./HerkunftPille";
import { GastAntwortChip, UngelesenPunkt, ungeleseneGastAntwort } from "./GastAntwortZeichen";
import { NotizZeile } from "./NotizZeile";
import { GrundPilleEtikett } from "./GrundPille";
import { grundPille, zustellZeile } from "../lib/grund-pille";
import { listenMonat } from "../lib/anfrage-angaben";
import { geraetIcon, geraetLabel, kartenAufrufe } from "../lib/einblicke";
import { zahlwegText } from "../lib/zahlweg-text";

/**
 * Die vier Aktionen des "⋯"-Menues (Antoine, 10.09.2026, s. `aufgaben.md` "Status 10.09.2026,
 * ~19:20"): "Angebot ansehen"/"Bearbeiten" sind EIN UND DIESELBE Zielroute (nur die Beschriftung
 * wechselt je nach Versandstatus), deshalb hier ein gemeinsamer Aktions-Schluessel statt zweier.
 * "Erinnerung setzen" ist bewusst KEIN eigener Punkt - Teil von "Notiz hinzufuegen".
 */
/**
 * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md, Antoines Mockup-Freigabe 13.09.2026): vierter
 * Punkt "spam" ("Als Spam löschen") — nur in der Spalte "du bist dran", solange noch kein Angebot
 * versendet wurde UND die Anfrage nicht manuell angelegt ist. Wie die anderen drei Punkte fuehrt
 * die Karte nichts selbst aus - `inquiries.tsx` (`fuehreMenuAktionAus`) ruft nach Bestaetigung
 * `DELETE /api/events/:id`.
 */
export type KanbanMenuAktion = "ansehen_bearbeiten" | "notiz" | "antworten" | "absagen" | "spam";

export interface AnfragenKanbanProps {
  abschnitte: AnzeigeAbschnitt[];
  /**
   * Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`): die Karte bekommt eine ZIEL-ADRESSE statt eines
   * Klick-Rueckrufs. Vorher hiess dieser Prop `onZeile` und fuehrte zu `navigate(...)` — der
   * Browser kannte kein Linkziel, deshalb tat Cmd-/Strg-/Mittelklick nichts.
   */
  zeilenZiel: (anfrage: Inquiry) => string;
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  onMenuAktion: (anfrage: Inquiry, aktion: KanbanMenuAktion) => void;
  /**
   * WARTESCHLANGE Punkt 25 (15.09.2026) — Klick auf die Glocke „Erinnerung schicken" (VOR dem
   * `mailto:`-Öffnen selbst, s. `KartenIconKnopf.onKlick` unten). Fire-and-forget aus Sicht der
   * Karte: sie ruft den Server-Aufruf/Neuladen nicht selbst auf, sondern reicht den Klick nur
   * weiter — derselbe Aufbau wie `onAktion`/`onMenuAktion` oben (die Seite entscheidet, was
   * passiert, die Karte navigiert/meldet nur).
   */
  onErinnerungSchicken: (anfrage: Inquiry) => void;
}

/**
 * Nur der ANZEIGE-Text der dritten Spalte weicht hier von der Regel ab (s. Kopfkommentar) — Titel
 * der anderen beiden Spalten kommen unveraendert aus `abschnitt.titel`.
 */
const KANBAN_SPALTEN_TITEL: Partial<Record<KanbanSpalte, string>> = {
  entschieden: "Zugesagt",
};

/**
 * Antoine, 12.10.2026: eine eigene Spalte „Abgelaufen" direkt nach „Kunde ist dran". Sie gibt es
 * NUR auf dem Board — die Regel (`@maestro/anfrage-stand`) und die Listenansicht bleiben bei
 * ihren Gruppen. Hinein gehören (a) offene Angebote, deren Frist verstrichen ist, und (b) ALLE
 * Vorgänge „Keine Antwort" (`stand.regel === "ohne_antwort"`, d. h. `events.status =
 * 'no_response'`) — „dort alle zurückholen". Wie jede Karte verschwinden sie erst, wenn der
 * Termin vorbei ist (`kanbanZeigt`).
 */
export type KanbanSpalte = AnzeigeGruppe | "abgelaufen";

/** Liegt die Angebotsfrist vor heute? Dieselbe Rechnung für Spalte und Kartenhinweis. */
function fristVerstrichen(offerValidUntil: string | null | undefined, jetzt: Date): boolean {
  if (!offerValidUntil) return false;
  const ziel = new Date(offerValidUntil);
  if (Number.isNaN(ziel.getTime())) return false;
  const tag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return tag(ziel) < tag(jetzt);
}

/** Gehört die Karte in die Spalte „Abgelaufen"? (s. `KanbanSpalte`) */
export function istAbgelaufen(anfrage: Pick<Inquiry, "stand" | "offerValidUntil">, heute: Date = new Date()): boolean {
  if (anfrage.stand?.regel === "ohne_antwort") return true;
  return anfrage.stand?.gruppe === "kunde_ist_dran" && fristVerstrichen(anfrage.offerValidUntil, heute);
}

/**
 * Farbige Pille je Spalte — dieselben Ton-Paare wie ueberall sonst (`PILL_CLASSES`, `./format.ts`),
 * keine neue Farbe erfunden. „Du bist dran" traegt den Akzentton (dieselbe Aussage wie der
 * gefuellte „Angebot überprüfen"-Knopf: hier ist etwas zu tun), „Kunde ist dran" den Info-Ton
 * (wartend), „Zugesagt" den Erfolgston (`success`, wie `EVENT_STATUS.offer_chosen`).
 */
const SPALTEN_TON: Record<KanbanSpalte, PillTone> = {
  du_bist_dran: "primary",
  kunde_ist_dran: "info",
  // Derselbe Warnton wie der Kartenhinweis „Frist abgelaufen … – nachfassen" (`text-error`).
  abgelaufen: "danger",
  entschieden: "success",
};

/**
 * Bughunt 09.09.2026, Nachschlag (Antoine: "es geht um das komplette Design, nicht nur um die
 * zwei fehlenden Elemente") — der Spaltenkopf stand im Mockup NEBENEINANDER (Pille links,
 * Untertitel rechts, `justify-content:space-between`), die erste Umsetzung stapelte beides
 * stattdessen untereinander. Kein eigener Karten-Rahmen um den Kopf (der sitzt im Mockup direkt
 * auf dem Seitenhintergrund, nicht in einer Box) — dafuer entfaellt hier auch der aeussere `Card`-
 * Rahmen um die ganze Spalte (s. `AnfragenKanban` unten).
 */
/**
 * WAS AUF DEM BOARD STEHT — UND DAMIT, WAS SUMMIERT WIRD. EINE MENGE, EINE STELLE.
 *
 * Antoine am 27.09.2026: „Unter ‚Zugesagt‘ stehen etwas über 16.000 €, aber allein ein zugesagter
 * Auftrag hat 20.000 €." Gemessen (Prod, STORIA, rein lesend): die Spalte zeigte 17 Karten über
 * 85.044,42 €, die Summe darüber 16.426,92 €. Die Summe hatte ihre EIGENE Auswahl: sie ließ jeden
 * Vorgang mit verstrichener Angebotsfrist weg (11 Karten, 66.748,50 € — darunter der Auftrag über
 * 20.170,50 €, zugesagt, Termin 08.10.2026, Angebotsfrist 22.09.2026) und zog bereits Bezahltes ab
 * (1.869,00 €). Die Karten standen trotzdem da. Zwei Mengen für eine Spalte — genau das darf nicht
 * sein.
 *
 * Seit diesem Fix entscheidet NUR `kanbanZeigt`, was auf dem Board steht; Karten, Spaltenkopf
 * (Summe und Untertitel) und die Geldzeile darüber lesen alle dieselbe gefilterte Menge
 * (`AnfragenKanban` unten). Nicht auf dem Board stehen:
 *
 *   1. **Abgelehnte, abgesagte/stornierte und unbeantwortete Vorgänge** (`gruppe === "abgelehnt"`)
 *      — sie bringen kein Geld (Antoine, 21.09.2026: „Sind hier in der Summe die verloren mit drin?
 *      Das darf nicht sein."). Sie bleiben in der Listenansicht auffindbar.
 *   2. **Vergangene Vorgänge** — Termin vor heute, auf den Kalendertag gerechnet (ein Termin
 *      „heute" zählt noch). Ohne Termin gilt ein Vorgang als NICHT vergangen: im Zweifel nicht
 *      ausschließen.
 *
 * Eine verstrichene ANGEBOTSFRIST ist dagegen kein Ausschlussgrund mehr: ein angenommenes Angebot
 * verfällt nicht (der 20.000-€-Auftrag oben ist echtes Geld, das noch kommt), und ein offenes,
 * abgelaufenes Angebot steht weiter als Karte da — dann muss es auch in der Summe stehen.
 */
export function kanbanZeigt(anfrage: Pick<Inquiry, "stand" | "eventDate">, heute: Date = new Date()): boolean {
  // Ausnahme seit 12.10.2026: „Keine Antwort" steht in der Spalte „Abgelaufen" (s. `KanbanSpalte`).
  if (anfrage.stand?.gruppe === "abgelehnt" && anfrage.stand.regel !== "ohne_antwort") return false;
  return !vorHeute(anfrage.eventDate, heute);
}

/**
 * Die Summe über einer Spalte ist GENAU die Summe der Beträge auf ihren Karten: derselbe Wert
 * (`anfrage.amountTotalCents`, server-autoritativ aus `apps/api/src/lib/anfragen-abfrage.ts` —
 * `events.amount_total_cents`, sonst der Betrag der GEWÄHLTEN Angebotsvariante; nie die Summe
 * mehrerer Varianten), über dieselben Zeilen. Keine eigene Auswahl, kein Abzug — was hier nicht
 * zählen soll, darf gar nicht erst als Karte in `zeilen` stehen (`kanbanZeigt`). `null` zählt 0
 * (kein Angebot = kein Betrag auf der Karte).
 */
export function spaltenSumme(zeilen: readonly { anfrage: Pick<Inquiry, "amountTotalCents"> }[]): number {
  return zeilen.reduce((summe, z) => summe + (z.anfrage.amountTotalCents ?? 0), 0);
}

/**
 * Migration 731: die Summe im Spaltenkopf als Text. Eine „À la carte“-Karte hat keinen Betrag und
 * zählt 0 in die Summe (Umsatzzahlen bleiben unverfälscht); besteht eine Spalte NUR aus solchen
 * Karten, steht dort „à la carte“ statt „0,00 €“.
 */
export function spaltenSummeText(zeilen: readonly { anfrage: Pick<Inquiry, "amountTotalCents" | "tatsachen"> }[]): string {
  const summe = spaltenSumme(zeilen);
  if (summe === 0 && zeilen.length > 0 && zeilen.every((z) => z.anfrage.tatsachen?.aLaCarte === true)) return A_LA_CARTE_KLEIN;
  return money(summe);
}

/**
 * Liegt ein Datum vor dem heutigen Kalendertag? `event_date`/`offer_valid_until` sind DATEs
 * ("2026-09-21" oder "2026-09-21T00:00:00.000Z") — verglichen wird nur der Tag, nicht die Uhrzeit.
 * Dieselbe Schwelle wie `list_expired_offers` (`sql/149_offer_expiry_sweep.sql:47`). Fehlt das
 * Datum oder ist es unlesbar: **nein**.
 */
export function vorHeute(isoDatum: string | null | undefined, heute: Date): boolean {
  if (!isoDatum) return false;
  const tag = String(isoDatum).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tag)) return false;
  const heuteTag = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, "0")}-${String(heute.getDate()).padStart(2, "0")}`;
  return tag < heuteTag;
}

function SpaltenKopf({
  gruppe,
  titel,
  untertitel,
  summeCents,
  summeText,
  hinweis,
}: {
  gruppe: KanbanSpalte;
  titel: string;
  untertitel: string;
  summeCents: number;
  /** Migration 731: überschreibt die Anzeige der Summe („à la carte“ statt „0,00 €“). */
  summeText?: string;
  /** Punkt 79, Zusatz 1 und 2: die eine Zahl, die unter dieser Spalte zaehlt. `null` = keine. */
  hinweis?: string | null;
}) {
  const titelAnzeige = KANBAN_SPALTEN_TITEL[gruppe] ?? titel;
  return (
    <div className="space-y-0.5 px-0.5">
      {/* Entwurf 40b (21.09.2026): Pille und Anzahl stehen dort NEBENEINANDER links („Du bist dran
          12 Anfragen"), die Summe allein rechts am Rand — bis hierher hing die Anzahl
          rechtsbuendig an der Summe und las sich wie deren Beschriftung. `items-baseline` setzt
          die drei verschieden grossen Schriften auf eine gemeinsame Grundlinie, wie im Entwurf. */}
      <div className="flex items-baseline gap-2.5">
        {/* Bleibt ein echtes <h3> — nur optisch als Pille gestylt (E2E-Test sucht die Heading-Rolle). */}
        <h3
          className={`inline-flex w-fit shrink-0 items-center rounded-full px-2.5 py-1 text-[12.5px] font-semibold ${PILL_CLASSES[SPALTEN_TON[gruppe]]}`}
        >
          {titelAnzeige}
        </h3>
        <p className="min-w-0 flex-1 truncate font-body-sm text-[12px] text-on-surface-variant/80">{untertitel}</p>
        <span data-testid="spalten-summe" className="shrink-0 whitespace-nowrap text-[12.5px] font-bold text-on-background dark:text-inverse-on-surface">
          {summeText ?? money(summeCents)}
        </span>
      </div>
      {/* Antoine, 13.10.2026: „Alle Boxen müssen auf derselben Höhe beginnen." Die Hinweiszeile
          steht darum in JEDER Spalte — ohne Hinweis leer (geschütztes Leerzeichen, gleiche
          Zeilenhöhe), sonst beginnt die erste Karte dort eine Zeile höher. */}
      {hinweis ? (
        <p data-testid={`spalten-hinweis-${gruppe}`} className="text-[11.5px] font-medium text-[#6F675A] dark:text-[#b8ae9e]">
          {hinweis}
        </p>
      ) : (
        <p data-testid={`spalten-hinweis-platz-${gruppe}`} aria-hidden="true" className="text-[11.5px] font-medium">
          {" "}
        </p>
      )}
    </div>
  );
}

/**
 * Punkt 79, Zusatz 1 (freigegeben am 20.09.2026): „N fertig, nicht versendet" unter der ersten
 * Spalte — laut Auftrag „der teuerste Zustand im Haus: ein fertiges Angebot, das niemand
 * abgeschickt hat".
 *
 * Die Zahl wird NICHT neu hergeleitet: `stand.regel` kennt den Zustand als eigene Zeile
 * (`angebot_unversendet`). Im Browser nachzurechnen, was „fertig" heisst, waere eine zweite
 * Wahrheit neben der Regel — und genau die trennt heute schon „fertig" von „angelegt, aber leer".
 */
function zaehleUnversendet(zeilen: readonly { stand: AnfrageStand }[]): number {
  return zeilen.filter((z) => z.stand.regel === "angebot_unversendet").length;
}

/**
 * Punkt 79, Zusatz 2 (freigegeben): „N Anzahlungen ueberfaellig · Betrag" unter der dritten
 * Spalte. „Direkt Geld", so der Auftrag.
 *
 * Die ZAEHLUNG steht in den Tatsachen (`anzahlungUeberfaelligSeit`), der Auftrag nennt sie
 * ausdruecklich „heute schon lieferbar". Der BETRAG ist die Angebotssumme des Vorgangs, nicht die
 * Anzahlungshoehe — die liegt heute in keiner Antwort dieser Liste. Deshalb steht hier bewusst
 * KEIN Betrag: eine Angebotssumme als „ueberfaelligen Anzahlungsbetrag" auszugeben waere eine
 * falsche Zahl an einer Stelle, an der es um Geld geht. Der Betrag kommt nach, sobald die
 * Anzahlungshoehe mitgeliefert wird (im Auftrag als „kleine Erweiterung der Abfrage" vermerkt).
 */
function zaehleAnzahlungUeberfaellig(zeilen: readonly { anfrage: Inquiry }[]): number {
  return zeilen.filter((z) => Boolean(z.anfrage.tatsachen?.anzahlungUeberfaelligSeit)).length;
}

/**
 * Welcher Hinweis gehoert unter DIESE Spalte? Hoechstens einer, und nur wenn er etwas zu sagen
 * hat — eine Zeile „0 fertig, nicht versendet" waere Rauschen und genau das, was Punkt 76 gerade
 * beseitigt hat.
 */
function spaltenHinweis(gruppe: KanbanSpalte, zeilen: readonly { anfrage: Inquiry; stand: AnfrageStand }[]): string | null {
  if (gruppe === "du_bist_dran") {
    const n = zaehleUnversendet(zeilen);
    return n > 0 ? (n === 1 ? "1 fertig, nicht versendet" : `${n} fertig, nicht versendet`) : null;
  }
  if (gruppe === "kunde_ist_dran") {
    const n = zaehleNachfassenFaellig(zeilen);
    return n > 0 ? (n === 1 ? "bei 1 ist Nachfassen fällig" : `bei ${n} ist Nachfassen fällig`) : null;
  }
  if (gruppe === "entschieden") {
    const n = zaehleAnzahlungUeberfaellig(zeilen);
    return n > 0 ? (n === 1 ? "1 Anzahlung überfällig" : `${n} Anzahlungen überfällig`) : null;
  }
  return null;
}

/**
 * Punkt 79, Zusatz 5 (freigegeben am 21.09.2026): die stillen Vorgaenge als Menge unter der
 * mittleren Spalte.
 *
 * ABWEICHUNG VOM WORTLAUT DES AUFTRAGS, bewusst und hier begruendet: dort steht „N seit > 2
 * Werktagen ungelesen". Genau dazu sagt derselbe Auftrag aber auch: „Die Werktagsrechnung (mit
 * Feiertagen je Bundesland) gibt es nur auf der Serverseite — sie gehoert dorthin, nicht in den
 * Browser." Beides zugleich geht nur so: Der Server rechnet die Werktage bereits (mit Feiertagen
 * des jeweiligen Bundeslands) und stellt das Ergebnis als Zeit-Chip „Nachfassen faellig" an jede
 * Zeile. Diese Zaehlung liest genau ihn.
 *
 * Damit gibt es EINE Werktagsrechnung statt zweier, die auseinanderlaufen koennen — und die
 * Aussage ist dieselbe: welche Vorgaenge liegen still und muessten angefasst werden.
 */
function zaehleNachfassenFaellig(zeilen: readonly { stand: AnfrageStand }[]): number {
  return zeilen.filter((z) => z.stand.chip === "Nachfassen fällig").length;
}

interface KanbanKarteProps {
  anfrage: Inquiry;
  stand: AnfrageStand;
  /** Welche Spalte diese Karte gerade zeigt — nur "du_bist_dran" bietet "Als Spam löschen" an. */
  gruppe: KanbanSpalte;
  zeilenZiel: (anfrage: Inquiry) => string;
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  onMenuAktion: (anfrage: Inquiry, aktion: KanbanMenuAktion) => void;
  onErinnerungSchicken: (anfrage: Inquiry) => void;
}

/**
 * Bughunt 09.09.2026 (Antoine, Live-Abgleich gegen das freigegebene Mockup): die erste Fassung
 * dieser Karte liess zwei Mockup-Elemente beim Bauen leise wegfallen (Kommentar unten dokumentierte
 * es nur als Randnotiz, nie als offene Abweichung an Antoine gemeldet) — genau die Luecke, die die
 * Karten trotz korrektem Deploy wie das alte Design wirken liess:
 *  1. Die farbige Status-Pille oben rechts fehlte fuer ALLE Karten ohne bereits versendetes Angebot
 *     (die ganze Spalte „Du bist dran") komplett, es gab nur `AngebotVersendetBadge` fuer den
 *     Versendet-Fall. Ersetzt durch `statusPilleFuerKarte()` unten — EIN Ort fuer die Pille in
 *     jedem Zustand, keine zweite Regel: sie liest ausschliesslich bereits von
 *     `@maestro/anfrage-stand` berechnete Felder (`angebotVersendetAm`, `aktion.schluessel`,
 *     `gruppe`) plus das vorhandene `anfrage.guests`-Feld, erfindet keinen neuen Zustand.
 *  2. Die Gaestezahl fehlte im Datum-Zeile komplett (Mockup zeigt sie rechts neben dem Datum bzw.
 *     "Gästezahl fehlt", wenn `anfrage.guests` null ist).
 */
function statusPilleFuerKarte(stand: AnfrageStand, anfrage: Inquiry): StatusMeta | null {
  /*
   * Buchungsstrecke D2: ein heißer Vorgang (Zeile 19) trägt statt „Angebot gesendet · …“ die
   * Tatsache, die ihn heiß macht — „3× angesehen“. Akzentton, nicht Rot: Rot bleibt echten
   * Fehlern (§ 6, Schlusssatz). Die Zahl kommt aus den Tatsachen des Servers, nicht gerechnet.
   */
  if (stand.regel === "heiss_anrufen") {
    // Einblicke (730): gemeint sind die Aufrufe des AKTUELLEN Angebots (seit dem letzten Versand).
    const n = anfrage.tatsachen?.aufrufeSeitVersand ?? anfrage.tatsachen?.aufrufe;
    return { label: typeof n === "number" ? `${n}× angesehen` : "Mehrfach angesehen", tone: "primary" };
  }
  // 12.10.2026 (Mimmo): „Zugesagt“ vor „Angebot gesendet“ — eine gebuchte Karte hat fast immer
  // ein versendetes Angebot und stand sonst gelb in der grünen Spalte.
  if (stand.gruppe === "gebucht") return { label: "Zugesagt", tone: "success" };
  if (stand.angebotVersendetAm) {
    return { label: `Angebot gesendet · ${stand.angebotVersendetAm}`, tone: "warn" };
  }
  if (anfrage.guests == null) return { label: "Gästezahl fehlt", tone: "neutral" };
  const schluessel = stand.aktion?.schluessel;
  if (schluessel === "angebot_senden" || schluessel === "angebot_bearbeiten") {
    return { label: "Angebot fertig", tone: "warn" };
  }
  // 22.09.2026: „keine Antwort" ist keine Ablehnung — niemand hat Nein gesagt. Die Karte steht
  // jetzt ueberhaupt erst in „Entschieden" (Zeile 16, @maestro/anfrage-stand); sie dort mit
  // „Abgelehnt" zu beschriften waere die zweite Haelfte desselben Fehlers.
  if (stand.regel === "ohne_antwort") return { label: "Keine Antwort", tone: "neutral" };
  if (stand.gruppe === "abgelehnt") return { label: "Abgelehnt", tone: "neutral" };
  /*
   * Punkt 79, Zusatz 7 (freigegeben am 21.09.2026): „Neu" fuellt eines von zwei Loechern, in
   * denen die Karte bisher GAR KEINE Pille zeigte — eine eben eingegangene Anfrage, an der alles
   * vollstaendig ist und an der noch nichts passiert ist.
   *
   * AUS DER REGEL, NICHT AUS DEM ROHEN STATUS. Der Auftrag sagt es ausdruecklich: „‚Neu' muss aus
   * der Regel kommen, nicht aus dem alten rohen Status-Vokabular — das wurde bewusst abgeschafft,
   * weil es log." Genau daran ist Punkt 71 aufgefallen: ein durchgefuehrter, voll bezahlter
   * Vorgang stand in der Arbeitsspalte, weil der rohe Status etwas anderes behauptete als die
   * Tatsachen.
   *
   * ZWEI Zeilen sagen „eingegangen, noch kein Angebot": `kein_angebot_vollstaendig` (mit
   * mitgeschickter Nachricht) und `kein_angebot_rueckfrage` (ohne). Der eigene Test hat das
   * korrigiert — der erste Entwurf kannte nur die erste und liess eine Anfrage ohne Nachricht
   * ohne jede Pille stehen, also genau das Loch, das dieser Zusatz fuellen soll.
   *
   * Die dritte Nachbarzeile `kein_angebot_gaeste_fehlen` bleibt aussen vor: dort zeigt die Karte
   * bereits „Gaestezahl fehlt" (oben), und das ist die genauere Auskunft.
   *
   * DIE ZWEITE PILLE AUS DEM ENTWURF, „Bezahlt", FEHLT BEWUSST. Sie waere heute nicht belegbar:
   * die Anfragenliste kennt keinen Zahlungsstand, und der rohe Vorgangsstatus taugt dafuer nicht
   * — Checkout-Vorgaenge erreichen `paid` nie (Migration 135/343, bewusst). Eine Pille „Bezahlt"
   * daraus zu bilden hiesse, bei Geld zu raten. Sie kommt, sobald die Liste den Zahlungsstand
   * mitliefert.
   */
  if (stand.regel === "kein_angebot_vollstaendig" || stand.regel === "kein_angebot_rueckfrage") return { label: "Neu", tone: "info" };
  return null;
}

/**
 * Icon je Aktions-Schluessel fuer die „Naechster Schritt"-Leiste unten (Redesign 14.09.2026,
 * Variante 40a). Nur die DREI KI-Aktionen (`angebot_erstellen_ki`/`rueckfrage_schreiben`/
 * `nachfassen` — alle mit „✨" beginnende `AKTION_TEXT`, `@maestro/anfrage-stand`) zeigen laut
 * Mockup KEIN Icon-SVG, nur das Emoji im Text selbst — fuer die brauchen diese Map keinen
 * Eintrag, `NaechsterSchrittLeiste` unten prueft `aktion.text.startsWith("✨")` VOR dem Lookup.
 */
const AKTION_ICON: Partial<Record<AktionSchluessel, string>> = {
  angebot_erstellen: "edit_document",
  angebot_bearbeiten: "edit",
  angebot_senden: "send",
  adresse_korrigieren: "edit_location_alt",
  verloren_schliessen: "cancel",
  anzahlung_anfordern: "payments",
  veranstaltung_oeffnen: "event",
  grund_notieren: "edit_note",
  jetzt_anrufen: "call",
};

/**
 * WARTESCHLANGE Punkt 22 (14.09.2026, Antoine, Screenshot eines Uhr-Icon-Chips „Gültig bis
 * 22.09.2026") — Datenquelle `events.offer_valid_until` (`anfragen-abfrage.ts`, dasselbe Feld, das
 * `offer-expiry-sweep.ts` für den automatischen Verfall liest). Tagesgenauer Vergleich in der
 * lokalen Zeitzone des Geräts — dasselbe Muster wie `daysUntil`/`istVeranstaltungstag` (`./format.ts`),
 * keine neue Datumslogik erfunden. `null` ⇒ kein Chip (kein bekanntes Ablaufdatum).
 */
function gueltigBisText(offerValidUntil: string | null | undefined, jetzt: Date = new Date()): string | null {
  if (!offerValidUntil) return null;
  const ziel = new Date(offerValidUntil);
  if (Number.isNaN(ziel.getTime())) return null;
  const tag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const abgelaufen = fristVerstrichen(offerValidUntil, jetzt);
  /*
   * Antoine, 12.10.2026: ein abgelaufenes Angebot wird NICHT mehr automatisch auf „Keine Antwort"
   * gestellt (es verschwand sonst vom Board). Es bleibt in „Kunde ist dran" — und die Karte sagt
   * deutlich, was zu tun ist. Das Datum bleibt stehen (s. Punkt 79 unten). Die Warnfarbe haengt
   * an `fristAbgelaufen` unten, nicht am Text.
   */
  if (abgelaufen) return `Frist abgelaufen seit ${formatDate(offerValidUntil)} – nachfassen`;
  /*
   * Punkt 79, Zusatz 4 (freigegeben am 20.09.2026): „läuft in N Tagen ab" statt nur „Gültig bis
   * TT.MM.JJJJ" — aus dem Auftrag: „Ein Countdown wird gelesen, ein Datum wird überlesen."
   *
   * Das DATUM BLEIBT DABEI STEHEN. Ein reiner Countdown wäre eine Verschlechterung: „läuft in 5
   * Tagen ab" beantwortet nicht, welches Datum im Angebot steht, und genau danach sucht der
   * Betrieb, wenn der Gast anruft. Der Countdown kommt also DAZU, er ersetzt nichts — dieselbe
   * Zurückhaltung wie in Punkt 75, wo Verdichtungen zurückgenommen wurden, die etwas wegnahmen.
   *
   * Ab dreißig Tagen entfällt er: „läuft in 84 Tagen ab" drängt sich auf, ohne zu drängen.
   */
  const tage = Math.round((tag(ziel) - tag(jetzt)) / 86_400_000);
  const countdown = tage === 0 ? "läuft heute ab" : tage === 1 ? "läuft morgen ab" : tage <= 30 ? `läuft in ${tage} Tagen ab` : null;
  return countdown ? `Gültig bis ${formatDate(offerValidUntil)} · ${countdown}` : `Gültig bis ${formatDate(offerValidUntil)}`;
}

/**
 * Punkt 79, Zusatz 3 (freigegeben am 20.09.2026): „Nie geöffnet" / „dreimal angesehen" auf der
 * Karte — aus dem Auftrag „die entscheidende Auskunft für ‚nachfassen oder nicht'".
 *
 * DIE UNTERSCHEIDUNG, AN DER ALLES HÄNGT: `null` heißt **nicht erhoben**, `0` heißt **gezählt und
 * nie geöffnet**. Aus „nicht erhoben" ein „nie geöffnet" zu machen wäre eine erfundene
 * Beobachtung — und gerade sie würde zum Nachfassen verleiten. Bei `null` steht deshalb nichts.
 *
 * Erst ab dem Versand sinnvoll: vor dem Versand ist „nie geöffnet" keine Auskunft, sondern eine
 * Selbstverständlichkeit.
 */
function aufrufeText(tatsachen: Inquiry["tatsachen"]): string | null {
  // Einblicke (Migration 730): „3× seit Versand · zuletzt gestern 18:40“ - das Geraet haengt die
  // Karte daneben mit Symbol an. Gezaehlt wird seit dem LETZTEN Versand (der Zaehler am Vorgang
  // laeuft ueber alle Versaende weiter).
  return kartenAufrufe({
    versendet: Boolean(tatsachen?.versendetAm),
    seitVersand: tatsachen?.aufrufeSeitVersand,
    insgesamt: tatsachen?.aufrufe,
    zuletztAm: tatsachen?.zuletztAngesehenAm,
  });
}

/**
 * Der Zahlungsstand auf der Karte. Antoine am 21.09.2026, auf die Frage, warum die Karten keine
 * Kennzeichnung „Bezahlt" tragen: **„Warum sollte sie das nicht ‚wissen'. Das System weiß doch was
 * bezahlt wurde und was nicht und vermerkt das dort."** Er hat recht — die Zahlen standen längst
 * im Zahlungsteil, sie fehlten nur in der Antwort dieser Liste. Seit sie mitkommen, steht hier
 * keine Schätzung, sondern die Summe, die auch die Abrechnung nennt.
 *
 * DREI ZUSTÄNDE, NICHT ZWEI:
 *   - gar keine Zahlung vermerkt → **nichts**. Ein Vorgang ohne jede Zahlungszeile ist nicht
 *     unbezahlt, sondern zahlungsfrei; „offen" wäre dort eine erfundene Forderung.
 *   - etwas eingegangen → der Betrag. NETTO, also nach Abzug von Erstattungen.
 *   - vermerkt, aber nichts eingegangen → **nichts an dieser Stelle**. Dass Geld fehlt, sagt
 *     bereits der Überfällig-Hinweis; eine zweite Zeile „0,00 € bezahlt" wäre nur Rauschen.
 *
 * Nur bei zugesagten Vorgängen: dort ist Geld die offene Frage. Davor gibt es in aller Regel noch
 * gar keine Zahlung, und wo doch, beantwortet sie nicht die Frage jener Spalte. Geprüft wird
 * `stand.gruppe === "gebucht"` und NICHT die Anzeigespalte — die dritte Spalte („entschieden")
 * enthält auch die abgesagten Vorgänge, und bei denen wäre „bezahlt" eine irreführende Meldung.
 */
function bezahltText(tatsachen: Inquiry["tatsachen"], stand: AnfrageStand): string | null {
  if (stand.gruppe !== "gebucht") return null;
  if (!tatsachen?.zahlungenVermerkt) return null;
  const cents = tatsachen.bezahltCents ?? 0;
  if (cents <= 0) return null;
  return `${money(cents)} bezahlt`;
}

/**
 * WARTESCHLANGE Punkt 25 (15.09.2026, Antoine, wörtlich: „In der mittleren Spalte, Kunde ist
 * dran, muss auch stehen, wann die letzte Erinnerung geschickt wurde.") — Datenquelle
 * `reminderNudgedAt` (`anfragen-abfrage.ts`, jüngster `activity_log`-Eintrag
 * `event.reminder_nudged`). Bewusst „angestoßen" statt „gesendet"/„geschickt": die Glocke ist ein
 * `mailto:`-Link (s. `erinnerungMailtoHref` unten) — was hier protokolliert wird, ist der Klick,
 * der das lokale Mailprogramm öffnet, NICHT der Beweis, dass der Nutzer die Mail dort auch
 * tatsächlich abgeschickt hat. „Gesendet" würde mehr Gewissheit vortäuschen, als die Anwendung
 * hat (vgl. die „Fehlendes wird gezeigt, nicht versteckt"-Transparenz-Philosophie,
 * `packages/angebots-verlauf`). Gleiches `null` ⇒ kein Chip-Muster wie `gueltigBisText`.
 */
function erinnerungAngestossenText(reminderNudgedAt: string | null | undefined): string | null {
  if (!reminderNudgedAt) return null;
  const datum = new Date(reminderNudgedAt);
  if (Number.isNaN(datum.getTime())) return null;
  return `Erinnerung angestoßen am ${formatDate(reminderNudgedAt)}`;
}

/**
 * Mangel 2 + 4 + 5 (Auftragskopf, Antoines acht verifizierte Maengel): ersetzt sowohl den
 * zweisaetzigen `StandSatz` (Mangel 2 — wiederholt Pille/Zeit ohne neue Information) als auch den
 * einzelnen `AktionsKnopf` (Mangel 4 — fuehrt IMMER in den Bearbeiten-Drawer) UND die leere
 * "Nichts zu tun"-Zeile (Mangel 5) durch EINE farbige Leiste mit Icon+Text. Absichtlich NICHT
 * `AktionsKnopf` aus `AnfragenGruppen.tsx` wiederverwendet — dieselbe Formulierung
 * (`stand.aktion.text`/`stand.regel === "wird_generiert"`) bleibt exakt gleich (keine zweite
 * Formulierung des Stands, nur eine zweite VISUELLE Behandlung fuer die Kanban-Karte).
 *
 * Farbe/Ton: amber (`PILL_CLASSES.warn`, exakt die Mockup-Hex-Werte `#96650D`/`#FAEFD9`) fuer
 * jede normale Aktion, Akzentton (`PILL_CLASSES.primary`) fuer die drei „✨"-KI-Aktionen — beide
 * Klassenketten bestehen bereits (`./format.ts`), hier keine neuen Farbwerte erfunden. Klickbar
 * nur, wenn eine echte Aktion vorliegt (`onKlick` ruft dasselbe `onAktion(anfrage, aktion)` wie
 * vorher der Knopf) — der Generierungs- und der Leerzustand sind reine Anzeige, kein Knopf.
 */
/**
 * Entwurf 40b (21.09.2026): die Leiste ist dort ein breiter Balken ueber die volle Kartenbreite in
 * einer eigenen Zeile. Sie bringt deshalb ihren eigenen Flex-Container mit — `flex-1` am Balken
 * wirkt nur innerhalb einer FLEX-Zeile waagerecht; als direktes Kind der senkrechten Karte wuerde
 * es ihn in die Hoehe ziehen statt in die Breite. Liefert die Leiste `null` (nichts zu tun), faellt
 * auch der Container weg — sonst entstuende eine leere Zeile mit sichtbarem Abstand.
 */
function NaechsterSchrittLeiste({ stand, onKlick }: { stand: AnfrageStand; onKlick: (aktion: StandAktion) => void }) {
  if (stand.regel === "wird_generiert") {
    return (
      <div className="flex items-center">
        <span
          className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-2.5 py-[7px] text-[12px] font-semibold ${PILL_CLASSES.neutral}`}
        >
          <Icon name="progress_activity" className="shrink-0 animate-spin text-[15px]" />
          <span className="truncate">Angebot wird erstellt</span>
        </span>
      </div>
    );
  }
  const aktion = stand.aktion;
  /**
   * WARTESCHLANGE Punkt 21 (14.09.2026, Antoine, wörtlich: „Den Button 'Nichts zu tun' braucht es
   * nicht, der ist überflüssig.") — der Leerzustand (kein `onClick`, aber optisch eine Pille wie
   * ein Knopf) zeigte bisher immer „Nichts zu tun", wenn `stand.aktion` leer ist. Jetzt: gar
   * nichts an dieser Stelle, kein Ersatztext. Seit Entwurf 40b steht die Leiste in einer eigenen
   * Zeile — faellt sie weg, faellt die ganze Zeile weg, und die Kurzwege darunter ruecken einfach
   * nach oben. Ein Ausrichtungs-Kunstgriff („justify-end") ist dafuer nicht mehr noetig.
   */
  if (!aktion) return null;
  const istKi = aktion.text.startsWith("✨");
  /*
   * Buchungsstrecke D2: „Jetzt anrufen“ — ein `tel:`-Link als echtes `<a>` (Telefon/Softphone
   * öffnet den Anruf), derselbe Balken im Akzentton wie die KI-Aktionen, damit er oben in der
   * Spalte auffällt. Kein Router-Ziel, deshalb nicht über `onKlick`.
   */
  if (aktion.ziel.startsWith("tel:")) {
    return (
      <div className="flex items-center">
        <a
          href={aktion.ziel}
          data-testid="karte-jetzt-anrufen"
          onClick={(e) => e.stopPropagation()}
          className={`${ZEILEN_AKTION} flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-2.5 py-[7px] text-left text-[12px] font-semibold motion-safe:transition-opacity hover:opacity-80 ${PILL_CLASSES.primary}`}
        >
          <Icon name="call" className="shrink-0 text-[15px]" />
          <span className="truncate">{aktion.text}</span>
        </a>
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onKlick(aktion);
        }}
        className={`${ZEILEN_AKTION} flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-2.5 py-[7px] text-left text-[12px] font-semibold motion-safe:transition-opacity hover:opacity-80 ${
          istKi ? PILL_CLASSES.primary : PILL_CLASSES.warn
        }`}
      >
        {!istKi && <Icon name={AKTION_ICON[aktion.schluessel] ?? "arrow_forward"} className="shrink-0 text-[15px]" />}
        <span className="truncate">{aktion.text}</span>
      </button>
    </div>
  );
}

/**
 * Zwei quadrate Icon-Knoepfe (30×30px) neben der „Naechster Schritt"-Leiste (Mockup, Variante
 * 40a): „Angebot öffnen" fuehrt zur selben Route wie der „⋯"-Menuepunkt Ansehen/Bearbeiten
 * (`angebotPfad`, `lib/pfade.ts` — keine zweite Zielberechnung), „Zahlungen" zur bestehenden
 * Abrechnungs-Unterseite des Vorgangs (`abrechnungPfad`). Beides sind bereits existierende
 * Routen — hier wird keine neue Ziel-Adresse erfunden (§ 7 des Bauplans „nur navigieren, was es
 * schon gibt"), nur ein direkter Link statt zwei Klicks (Mangel 4).
 */
/**
 * WARTESCHLANGE Punkt 19 (14.09.2026, Antoine) — das neue Glocken-Icon „Erinnerung schicken" ist
 * KEINE interne Route, sondern ein `mailto:`-Link (s. `erinnerungMailtoHref` unten). Statt einer
 * zweiten, fast identischen Komponente bekommt `KartenIconKnopf` ein optionales `href` — gesetzt,
 * rendert ein echtes `<a>` (extern/`mailto:`), sonst wie bisher ein `<Link>` auf `to` (interne
 * Route). Beide teilen sich Optik/Verhalten (Klick-Stop, 30×30px, Label/Title) vollstaendig.
 *
 * WARTESCHLANGE Punkt 25 (15.09.2026) — `onKlick`, NUR fuer den `href`-Zweig: die Glocke braucht
 * zusaetzlich zum `mailto:`-Oeffnen einen Server-Aufruf, der den Klick protokolliert
 * (`onErinnerungSchicken`, s. `KanbanKarte` unten). Bewusst KEIN `e.preventDefault()` — der
 * `mailto:`-Link soll sich weiterhin sofort und ohne Verzoegerung oeffnen, der Aufruf laeuft
 * daneben her (fire-and-forget, die aufrufende Seite entscheidet ueber Fehlerbehandlung/Neuladen).
 */
function KartenIconKnopf({
  to,
  href,
  label,
  icon,
  onKlick,
}: {
  to?: string;
  href?: string;
  label: string;
  icon: string;
  onKlick?: () => void;
}) {
  const className = `${ZEILEN_AKTION} flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface-variant motion-safe:transition-colors hover:bg-surface-container-high/50 dark:border-outline/40 dark:bg-surface-dim dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/20`;
  if (href) {
    return (
      <a
        href={href}
        aria-label={label}
        title={label}
        onClick={(e) => {
          e.stopPropagation();
          onKlick?.();
        }}
        className={className}
      >
        <Icon name={icon} className="text-[16px]" />
      </a>
    );
  }
  return (
    <Link to={to!} aria-label={label} title={label} onClick={(e) => e.stopPropagation()} className={className}>
      <Icon name={icon} className="text-[16px]" />
    </Link>
  );
}

/**
 * Entwurf 40b (21.09.2026): der zweite Einstieg in den Vorgang — im Entwurf „Angebot öffnen" — ist
 * dort ein BESCHRIFTETER Knopf in der Akzentfarbe, kein stummes Icon wie bisher. Dasselbe Ziel,
 * dieselbe Route, nur sichtbar benannt: der haeufigste naechste Griff soll ohne Raten erkennbar
 * sein. Antoine am 21.09.2026: „Es geht nur ums aussehen!!!" — entsprechend kommt hier keine
 * Aktion hinzu, es wird nur eine vorhandene lesbar gemacht.
 *
 * Auf sehr schmalen Spalten (Handy) bleibt nur das Icon stehen (`hidden sm:inline`) — die
 * Beschriftung wuerde dort die Zeile sprengen; `aria-label`/`title` tragen den Namen weiter, der
 * Knopf bleibt also auch dann benannt.
 */
function KartenAkzentLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      onClick={(e) => e.stopPropagation()}
      className={`${ZEILEN_AKTION} inline-flex h-[30px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-3 text-[12px] font-semibold text-on-primary shadow-sm motion-safe:transition-opacity hover:opacity-90`}
    >
      <Icon name={icon} className="shrink-0 text-[15px]" />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );
}

/**
 * WARTESCHLANGE Punkt 19 (14.09.2026, Antoine, wörtlich: „Bei ... unter 'Kunde ist dran' links von
 * Icon 'Angebot öffnen', ein Icon mit einer Glocke 'Erinnerung schicken'. Das soll zur Mail in der
 * Mitte führen, um eine Erinnerung zu schicken.") — dasselbe etablierte `mailto:`-Muster wie
 * `event-detail.tsx` („E-Mail schreiben", `mailto:${customer.email}`) bzw. `VorDemSendenKarte.tsx`
 * (`mailtoHref`, vorausgefuellter Betreff/Text via `encodeURIComponent`). Kein neuer Versandweg,
 * kein Server-Aufruf — oeffnet nur das lokale Mailprogramm mit einem Vorschlag, frei anpassbar vor
 * dem Senden. `null` ohne bekannte Kunden-E-Mail — das Icon bleibt dann ganz weg (s. `KanbanKarte`
 * unten, dasselbe Alles-oder-nichts-Muster wie `zeigtSpamOption`).
 */
function erinnerungMailtoHref(anfrage: Inquiry): string | null {
  const email = anfrage.customerEmail?.trim();
  if (!email) return null;
  const betreff = `Erinnerung: Ihr Angebot${anfrage.company?.trim() ? ` für ${anfrage.company.trim()}` : ""}`;
  const text = `Hallo ${anfrage.customerName},\n\nwir wollten uns kurz nach Ihrem Angebot erkundigen und freuen uns über eine Rückmeldung.\n\nViele Grüße`;
  return `mailto:${email}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
}

/**
 * Kanban-spezifische Karte — bewusst NICHT die geteilte `AnfrageKarte` (`AnfragenGruppen.tsx`,
 * auch von der Liste verwendet), damit eine Optik-Aenderung hier die Liste nicht ungewollt
 * mitveraendert (Auftrags-Vorgabe). `initialen` bleibt derselbe Baustein wie dort — `StandSatz`/
 * `AktionsKnopf` dagegen NICHT mehr (s. `NaechsterSchrittLeiste` oben, Redesign 14.09.2026).
 *
 * ── Redesign 14.09.2026 „Variante 40a — Kompakt" (Antoine, Mockup-Freigabe) ────────────────────
 * Acht verifizierte Maengel der Vorgaenger-Karte (Auftragskopf) beheben, u.a.: Angebotswert
 * rechtsbuendig in Zeile 1 (`anfrage.amountTotalCents`, server-autoritativ, `money()` — nie im
 * Web-Code neu gerechnet), keine E-Mail-Zeile mehr, Ort ergaenzt in Zeile 2 neben Datum/Gaeste,
 * Spaltensumme im Spaltenkopf (`SpaltenKopf` unten). Das „⋯"-Menue (Freigabe 11.09.2026) bleibt
 * unveraendert oben rechts, `z-[3]` unangetastet.
 *
 * Anlass (`anfrage.eventType`) steht NICHT auf der Karte — die exakte Mockup-Spezifikation
 * (Zeile 2) nennt nur drei Icon-Paare (Datum/Gaeste/Ort), obwohl der Auftragskopf „Anlass und Ort
 * fehlen" als urspruengliche Beschwerde nennt. Bewusst der woertlichen Spezifikation gefolgt statt
 * eigenmaechtig ein viertes Element ergaenzt — siehe Abschlussbericht/offene Punkte.
 */
function KanbanKarte({ anfrage, stand, gruppe, zeilenZiel, onAktion, onMenuAktion, onErinnerungSchicken }: KanbanKarteProps) {
  const firma = anfrage.company?.trim() || null;
  /**
   * WARTESCHLANGE (docs/WARTESCHLANGE.md, „Ort fehlt" trotz erkennbarer Lieferung/Im-Haus,
   * Konkretisierung 14.09.2026) — serverseitig berechneter Anzeige-Fallback
   * (`eventLocationAbgeleitet`, `apps/api/src/lib/anfragen-abfrage.ts`), NUR wenn der echte
   * `eventLocation`-Freitext leer ist. Kein Frontend-Rechenweg, kein Backfill — bleibt der
   * abgeleitete Wert ebenfalls leer (z. B. `locationType` noch unbekannt), zeigt die Karte
   * unveraendert „Ort fehlt".
   */
  const ortAbgeleitet = !anfrage.eventLocation?.trim() ? (anfrage.eventLocationAbgeleitet?.trim() || null) : null;
  const pille = statusPilleFuerKarte(stand, anfrage);
  const angebotVersendet = Boolean(stand.angebotVersendetAm);
  // F2: "Als Spam löschen" nur in "du bist dran" (vor Versand, keine manuell angelegte Anfrage) —
  // Antoines Mockup-Freigabe 13.09.2026.
  const zeigtSpamOption = gruppe === "du_bist_dran" && !angebotVersendet && anfrage.source !== "manual";
  /**
   * WARTESCHLANGE Punkt 19 — nur in „Kunde ist dran" sinnvoll (dort wartet der Betrieb auf eine
   * Reaktion des Kunden); ohne bekannte E-Mail bleibt das Icon ganz weg (Alles-oder-nichts, wie
   * `zeigtSpamOption`), kein deaktivierter Zustand.
   */
  const erinnerungHref = erinnerungMailtoHref(anfrage);
  // Seit 12.10.2026 auch in „Abgelaufen": dort ist Nachfassen genau die Aufgabe.
  const wartetAufKunde = gruppe === "kunde_ist_dran" || gruppe === "abgelaufen";
  const zeigtErinnerung = wartetAufKunde && Boolean(erinnerungHref);
  /**
   * WARTESCHLANGE Punkt 22 — bewusst NUR in „Kunde ist dran": `events.offer_valid_until` wird beim
   * Übergang nach „Zugesagt" (`offer_chosen`) serverseitig NICHT geleert (geprüft in
   * `routes/events.ts`, Übergang `to === "offer_chosen"` — kein Feld-Reset dort) und bleibt daher
   * ab dem Zusagezeitpunkt ein stehen gebliebenes, fachlich irrelevantes Datum: ein bereits
   * angenommenes Angebot kann nicht mehr „ablaufen" (der automatische Verfall,
   * `offer-expiry-sweep.ts`, prüft ohnehin nur `status = 'offer_sent'`). In „Zugesagt" würde die
   * Karte sonst ein zunehmend in der Vergangenheit liegendes Datum als „Abgelaufen seit ..."
   * zeigen, obwohl der Auftrag längst gewonnen ist — verwirrend statt hilfreich. Antoines eigene
   * Unsicherheit („wenn es Sinn macht bei 'Zugesagt'") wird hier mit Nein beantwortet.
   */
  const gueltigBis = wartetAufKunde ? gueltigBisText(anfrage.offerValidUntil) : null;
  // Am Text abgelesen, damit Farbe und Wortlaut nie auf zwei verschiedenen Tagesvergleichen beruhen.
  const fristAbgelaufen = gueltigBis?.startsWith("Frist abgelaufen") === true;
  /*
   * Punkt 79, Zusatz 3: wie `gueltigBis` daneben nur in „Kunde ist dran" — dort und nur dort
   * lautet die Frage „nachfassen oder nicht", und dafuer ist die Auskunft gemacht. In der ersten
   * Spalte ist noch nichts versendet, in der dritten ist entschieden.
   */
  const aufrufe = wartetAufKunde ? aufrufeText(anfrage.tatsachen) : null;
  /**
   * WARTESCHLANGE Punkt 25 — wie `gueltigBis` daneben nur in „Kunde ist dran" (dort ist das
   * Glocken-Icon ueberhaupt sichtbar/relevant), NUR wenn mindestens eine Erinnerung protokolliert
   * wurde (kein Platzhalter im leeren Fall).
   */
  const erinnerungAngestossen = wartetAufKunde ? erinnerungAngestossenText(anfrage.reminderNudgedAt) : null;
  /** Der Zahlungsstand — s. `bezahltText`. Nur bei Zugesagten, und nur wenn wirklich etwas einging. */
  const bezahlt = bezahltText(anfrage.tatsachen, stand);
  /** Zahlweg-Hinweis (Mimmo, 02.11.2026) — neben „€ bezahlt“, nur bei Zugesagten; Weg und Summe vom Server. */
  const zahlweg = stand.gruppe === "gebucht" ? zahlwegText(anfrage.tatsachen?.zahlweg, { bezahltCents: anfrage.tatsachen?.bezahltCents ?? 0, gesamtCents: anfrage.tatsachen?.betragCents ?? anfrage.amountTotalCents, ohneVollstaendig: true }) : null;
  /**
   * Eine fehlgeschlagene Zahlung steht in JEDER Spalte: sie ist keine Auskunft über den Fortschritt
   * des Vorgangs, sondern eine Störung. Sie zu verstecken, weil die Karte gerade in einer anderen
   * Spalte liegt, wäre genau das Wegsehen, das der Betreiber der Anwendung vorwirft.
   */
  const zahlungFehlgeschlagen = anfrage.tatsachen?.zahlungFehlgeschlagen === true;
  /** Antoine 19.10.2026: eine ungelesene Antwort des Gastes steht in JEDER Spalte (s. `GastAntwortZeichen`). */
  const gastAntwortAm = ungeleseneGastAntwort(anfrage);
  /**
   * Grund-Pille (Antoine, 20.10.2026, Mockup V5): in „Du bist dran“ sagt GENAU EINE Pille oben rechts
   * auf der Kartenkante, warum die Karte beim Betreiber liegt. Sie löst dort den Chip „Antwort vom
   * Gast“ ab (nicht doppelt zeigen); in den anderen Spalten bleibt der Chip, weil dort keine Pille
   * steht. Bei „Mail nicht zugestellt“ kommt ein dünner roter Rand und eine Zeile „Adresse · Grund“.
   */
  const pilleGrund = grundPille({ stand, tatsachen: anfrage.tatsachen });
  const zustellProblem = pilleGrund?.art === "zustellung";
  const zustellText = zustellProblem ? zustellZeile(anfrage.tatsachen) : null;
  const gastChipZeigen = Boolean(gastAntwortAm) && !pilleGrund;
  /**
   * Buchungsstrecke D1 — der Tempo-Alarm (Mockup Station 8: „Anfrage wartet seit 2 Std. 10 Min.“,
   * Karte rot). Der Text kommt fertig vom Server (`tempoAlarmStand`, apps/api/src/lib/tempo-alarm.ts);
   * hier wird nichts nachgerechnet. Er steht in derselben Lagezeile und Warnfarbe wie die
   * fehlgeschlagene Zahlung, und die Karte bekommt einen roten Rand.
   */
  const tempoAlarm = anfrage.tempoAlarm?.text?.trim() || null;
  /** Punkt 131: Schild „Große Gruppe“ — die Regel liegt beim Server (`istGrosseGruppe`). */
  const grosseGruppe = anfrage.grosseGruppe === true;
  /**
   * Buchungsstrecke C2 — Terminreservierung. `terminReservierung`: dieser Vorgang hält den Tag
   * („Termin reserviert bis 06.10.“). `terminKonflikt`: ein ANDERER Vorgang hält denselben Tag —
   * Warnfarbe, denn ein zweites Angebot für diesen Tag bricht die Zusage an den anderen Gast.
   * Beide Texte kommen fertig vom Server (lib/terminreservierung.ts); ohne laufende Reservierung
   * (also immer bei ausgeschaltetem Schalter) sind beide leer.
   */
  const terminReserviert = anfrage.terminReservierung?.text?.trim() || null;
  const terminKonflikt = anfrage.terminKonflikt?.text?.trim() || null;
  /**
   * Buchungsstrecke D2: heißer Vorgang ohne hinterlegte Nummer — die Regel bietet dann keinen
   * `tel:`-Link an (kein toter Anruf), die Karte sagt warum.
   */
  const heissOhneNummer = stand.regel === "heiss_anrufen" && stand.aktion?.schluessel !== "jetzt_anrufen";

  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Schliesst bei Klick ausserhalb ODER Escape — dasselbe simple, handgebaute Verhalten wie ein
  // natives Kontextmenue, ohne eine neue Abhaengigkeit (kein Radix-DropdownMenu im Bestand, s.
  // Kommentar oben an der Karte).
  useEffect(() => {
    if (!menuOffen) return;
    function aufAusserhalbKlick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOffen(false);
    }
    function aufEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOffen(false);
    }
    document.addEventListener("mousedown", aufAusserhalbKlick);
    document.addEventListener("keydown", aufEscape);
    return () => {
      document.removeEventListener("mousedown", aufAusserhalbKlick);
      document.removeEventListener("keydown", aufEscape);
    };
  }, [menuOffen]);

  function waehleMenuAktion(aktion: KanbanMenuAktion) {
    setMenuOffen(false);
    onMenuAktion(anfrage, aktion);
  }

  return (
    // Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`): die Karte war eine nachgebaute Schaltflaeche
    // (`role="button"` + `onClick`/`onKeyDown`) ohne jedes Linkziel — Cmd-/Strg-/Mittelklick und
    // „Link in neuem Tab öffnen" waren damit unmoeglich. Jetzt deckt ein echter `<Link>`
    // (`ZeilenLink`) die Kartenflaeche ab; `relative` war ohnehin schon gesetzt (fuer das
    // „⋯"-Menue), der Link braucht keinen zusaetzlichen Container.
    <Card
      data-testid="kanban-karte"
      data-tempo-alarm={tempoAlarm ? "ja" : undefined}
      className={`group relative flex flex-col gap-2.5 px-4 motion-safe:transition-shadow hover:shadow-[0_8px_18px_-6px_rgba(45,38,26,0.28)] dark:hover:shadow-[0_8px_18px_-6px_rgba(0,0,0,0.6)] ${
        pilleGrund ? "mt-2 pb-3.5 pt-[18px]" : "py-3.5"
      }${tempoAlarm ? " !border-error/60 bg-error/5 dark:bg-error/10" : zustellProblem ? " !border-error/45" : ""}`}
    >
      {/* Listenstandard (Brief § 6, Punkt 95): einheitlicher Fokusring statt der Umrisslinie. */}
      <ZeilenLink
        to={zeilenZiel(anfrage)}
        label={`Vorgang von ${anfrage.customerName} öffnen`}
        className="focus-visible:!outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#C2410C] dark:focus-visible:shadow-[0_0_0_2px_#262119,0_0_0_4px_#C2410C]"
      />
      {/* Bughunt 09.09.2026 (Antoine, Screenshot: Status-Pille ueberlagerte den Firmennamen) —
          Root Cause: ein `shrink-0`-Block mit unzerbrechlichem Pillentext neben `min-w-0 flex-1`
          zwang die Namensspalte in schmalen Mobile-Spalten enger als ein einzelnes Wort, das dann
          sichtbar (per `overflow: visible`) ueber die Pille hinauslief. Fix: `flex-wrap` auf der
          Kopfzeile — Zeitstempel+Pille rutschen als GANZES Element in eine eigene Zeile, sobald
          nicht genug Platz ist, statt die Namensspalte zu zerquetschen.

          Entwurf 40b (21.09.2026): das „⋯"-Menue sass bis hierher `absolute` oben rechts in der
          Kartenecke (daher das frueher noetige `pr-7` an dieser Zeile). Im Entwurf steht es unten
          in der Aktionszeile, am Ende der Kurzwege — es ist dorthin gewandert und laeuft damit im
          normalen Textfluss mit, statt ueber ihm zu schweben. Die frueheren z-index-Konflikte
          (Bughunt 14.09.2026: das geoeffnete Menue lag unter dem Aktionsknopf) sind damit
          gegenstandslos: das Menue steht jetzt IM selben Flex-Container wie die Knoepfe und
          spaeter im DOM als sie. Sein Dropdown oeffnet nach oben (`bottom-9`) statt nach unten,
          weil es am unteren Kartenrand sitzt. */}
      {pilleGrund && (
        <GrundPilleEtikett pille={pilleGrund} onKlick={pilleGrund.art === "gast_antwort" ? () => onMenuAktion(anfrage, "antworten") : undefined} />
      )}
      {/* Zeile 1 (Mockup „Kopf"): Avatar 30px · Name/Firma + Ansprechpartner/Zeit · Betrag. */}
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-surface-container-high text-[11px] font-bold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
          {initialen(anfrage.customerName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-snug text-on-background dark:text-inverse-on-surface">
            {gastAntwortAm && <UngelesenPunkt />}
            {firma ?? anfrage.customerName}
          </p>
          {/*
            Punkt 79, Aussehen nach Entwurf 40b (21.09.2026): Die Eingangszeit ist nach rechts
            unter den Betrag gewandert — dieselbe Angabe, anderer Platz.
            Hier bleibt der Ansprechpartner, ABER NUR wenn oben die Firma steht. Ohne Firma trägt
            die Titelzeile bereits den Namen; ihn hier zu wiederholen, stand kurz im Code und
            wurde sofort von vier bestehenden Prüfungen gefangen („Found multiple elements").
          */}
          {firma && (
            <p className="mt-0.5 truncate font-body-sm text-[11.5px] text-on-surface-variant/70 dark:text-secondary-fixed-dim">
              {anfrage.customerName}
            </p>
          )}
          <div className="mt-1">
            <HerkunftPille herkunft={anfrage.herkunft} isTest={anfrage.isTest} anfrage={anfrage} />
          </div>
        </div>
        {/* Angebotswert — server-autoritativ (`anfrage.amountTotalCents`), nie im Web-Code
            neu gerechnet, nur `money()`-Formatierung. `flex-none` (Mockup): schrumpft nie.
            Entwurf 40b: darunter die Eingangszeit, rechtsbündig und leiser. */}
        <div className="mt-0.5 flex-none text-right">
          <span data-testid="karten-betrag" className="block whitespace-nowrap text-[13.5px] font-bold text-on-background dark:text-inverse-on-surface">
            {anfrage.tatsachen?.aLaCarte === true ? A_LA_CARTE_KLEIN : money(anfrage.amountTotalCents)}
          </span>
          <span className="block whitespace-nowrap font-body-sm text-[11px] text-on-surface-variant/70 dark:text-secondary-fixed-dim">
            {timeAgo(anfrage.createdAt)}
          </span>
        </div>
      </div>

      {/* Zeile 2 (Mockup „Metazeile"): Datum/Gaeste/Ort links, Status-Pille rechtsbuendig. */}
      <div data-testid="karte-eckdaten" className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1 font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
          <Icon name="event" className="shrink-0 text-[14px] text-on-surface-variant/70" />
          {/* Punkt 130: nur ein Monat bekannt → „Dez“ / „Jan 2027“, der Tag bleibt offen. */}
          {anfrage.eventDate ? formatDate(anfrage.eventDate) : (listenMonat(anfrage) ?? "Datum offen")}
        </span>
        {/* V-1304: offener Vorgang mit Datum in der Vergangenheit — meist ein Tippfehler im Jahr. */}
        {datumPruefenNoetig(anfrage.eventDate, anfrage.tatsachen?.eventStatus, undefined, anfrage.dateEnd) && (
          <span data-testid="karte-datum-pruefen">
            <StatusPill meta={{ label: "Datum prüfen", tone: "warn" }} icon="event_busy" />
          </span>
        )}
        <span className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
          <Icon name="group" className="shrink-0 text-[14px] text-on-surface-variant/70" />
          {anfrage.guests != null ? `${anfrage.guests} Gäste` : "Gästezahl fehlt"}
        </span>
        {/* Ort: derselbe Fallback-Wortlaut wie bei der Gaestezahl daneben (Vorbild fuer den
            fehlenden Wert, Auftrag § Tests) — nicht das List-Muster `eckdatenZeile` (Feld
            komplett weglassen), sondern die bereits etablierte Kanban-eigene Konvention. Fehlt
            der echte Wert, aber der Ort-Typ ist bekannt (Im Haus/Lieferung), zeigt die Karte
            stattdessen den serverseitig abgeleiteten Wert (`ortAbgeleitet`) — gleiche Textfarbe
            wie ein normaler Wert (plausible, nicht geratene Adresse), nur mit Tooltip als
            dezenter Hinweis auf die Herkunft. */}
        <span
          className="flex min-w-0 items-center gap-1 font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"
          title={ortAbgeleitet ? "Automatisch ermittelt" : undefined}
        >
          <Icon name="location_on" className="shrink-0 text-[14px] text-on-surface-variant/70" />
          <span className="truncate">{anfrage.eventLocation?.trim() || ortAbgeleitet || "Ort fehlt"}</span>
          {/* WARTESCHLANGE Punkt 13 (15.09.2026): derselbe ✨-Marker wie beim Speisekarten-Import
              (menu-import.tsx) — der Ort-TYP (Im Haus/Lieferung) selbst ist ein unbestaetigter
              KI-Vorschlag (`event_field_versions.source='ai_suggestion'`), unabhaengig davon,
              welcher Text in der Zeile gerade angezeigt wird. Verschwindet von selbst, sobald ein
              Mensch den Ort-Typ bestaetigt/korrigiert (serverseitige Ableitung). */}
          {anfrage.locationTypeUnconfirmed && (
            <span className="shrink-0 text-[11px] text-secondary" title="KI-Vorschlag, noch nicht bestätigt">
              ✨
            </span>
          )}
        </span>
        <div className="ml-auto">{pille && <StatusPill meta={pille} />}</div>
      </div>

      {/*
        Zeile 3, Entwurf 40b (21.09.2026): DIE LAGE DES ANGEBOTS steht dort in einer EIGENEN
        Zeile unter den Eckdaten — im Entwurf nacheinander „Gültig bis 18.09.2026 · 0× geöffnet",
        bei anderen Karten „Noch kein Angebot" oder „Angenommen". Die Eckdatenzeile darüber trägt
        dort ausschließlich Datum, Gästezahl und Ort und rechtsbündig die Status-Pille.

        Bis hierher lief beides in EINER Zeile: bei einer Karte mit Termin, Gästezahl, Ort,
        Gültigkeit, Öffnungszähler und Zahlungsstand brach sie um, und der Umbruch fiel je nach
        Spaltenbreite jedesmal woanders — mal stand „Gültig bis" noch neben dem Ort, mal allein.
        Fest getrennt steht die Gültigkeit immer an derselben Stelle.

        REINE ANORDNUNG: dieselben Angaben, derselbe Wortlaut, dieselbe Bedingung, wann jede
        einzelne erscheint. Keine kommt hinzu, keine fällt weg (Antoine am 21.09.2026:
        „Übernimm aus dem Entwurf keine Funktionen und lass keine weg. Es geht nur ums
        aussehen!!!"). Die Zeile selbst entsteht nur, wenn mindestens eine der Angaben etwas zu
        sagen hat — sonst bliebe auf einer frischen Anfrage ein leerer Streifen stehen.

        `-mt-1` ist der Entwurfswert (`margin-top:-4px`): die Lagezeile rückt enger an die
        Eckdaten heran als der übrige Kartenabstand (`gap-2.5`), damit beide als ein Block lesen.
      */}
      {zustellText && (
        <p data-testid="karte-zustell-zeile" className="-mt-1 truncate font-body-sm text-[12px] font-medium text-error">
          {zustellText}
        </p>
      )}
      {(gastChipZeigen || tempoAlarm || grosseGruppe || terminKonflikt || terminReserviert || heissOhneNummer || bezahlt || zahlweg || zahlungFehlgeschlagen || gueltigBis || erinnerungAngestossen || aufrufe) && (
        <div data-testid="karte-lagezeile" className="-mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* Der Zahlungsstand (21.09.2026, Antoine: „Das System weiß doch was bezahlt wurde") —
              derselbe Icon+Text-Baustein wie die Angaben daneben, kein neuer Chip-Stil. Grün, weil
              es die einzige Angabe auf der Karte ist, die etwas ABGESCHLOSSENES meldet. */}
          {gastChipZeigen && gastAntwortAm && <GastAntwortChip am={gastAntwortAm} onKlick={() => onMenuAktion(anfrage, "antworten")} />}
          {bezahlt && (
            <span
              data-testid="karte-bezahlt"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-medium text-secondary dark:text-[#b8ae9e]"
            >
              <Icon name="paid" className="shrink-0 text-[14px]" />
              {bezahlt}
            </span>
          )}
          {zahlweg && (
            <span
              data-testid="karte-zahlweg"
              title={zahlweg.title}
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-medium text-secondary dark:text-[#b8ae9e]"
            >
              <Icon name={zahlweg.icon} className="shrink-0 text-[14px]" />
              {zahlweg.label}
            </span>
          )}
          {/* Eine fehlgeschlagene Zahlung ist eine Störung, keine Zwischenmeldung — sie steht in
              jeder Spalte und in der Warnfarbe. Ohne Betrag: welche der Zahlungen es traf, steht in
              der Abrechnung des Vorgangs; hier eine Summe zu nennen, hieße raten. */}
          {/* Buchungsstrecke D1: Tempo-Alarm — dieselbe Warnfarbe wie die fehlgeschlagene Zahlung. */}
          {tempoAlarm && (
            <span
              data-testid="karte-tempo-alarm"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-semibold text-error"
            >
              <Icon name="notifications_active" className="shrink-0 text-[14px]" />
              {tempoAlarm}
            </span>
          )}
          {/* Punkt 131: große Gruppe — Akzentfarbe, keine Warnung: eine Chance, kein Problem. */}
          {grosseGruppe && (
            <span
              data-testid="karte-grosse-gruppe"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-semibold text-primary"
            >
              <Icon name="groups" className="shrink-0 text-[14px]" />
              Große Gruppe
            </span>
          )}
          {/* Buchungsstrecke C2: Konflikt in Warnfarbe, eigene Reservierung neutral wie „Gültig bis“. */}
          {terminKonflikt && (
            <span
              data-testid="karte-termin-konflikt"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-semibold text-error"
            >
              <Icon name="event_busy" className="shrink-0 text-[14px]" />
              {terminKonflikt}
            </span>
          )}
          {terminReserviert && (
            <span
              data-testid="karte-termin-reserviert"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"
            >
              <Icon name="event_available" className="shrink-0 text-[14px] text-on-surface-variant/70" />
              {terminReserviert}
            </span>
          )}
          {heissOhneNummer && (
            <span
              data-testid="karte-keine-nummer"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"
            >
              <Icon name="phone_disabled" className="shrink-0 text-[14px] text-on-surface-variant/70" />
              Keine Nummer hinterlegt
            </span>
          )}
          {zahlungFehlgeschlagen && (
            <span
              data-testid="karte-zahlung-fehlgeschlagen"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-medium text-error"
            >
              <Icon name="credit_card_off" className="shrink-0 text-[14px]" />
              Zahlung fehlgeschlagen
            </span>
          )}
          {/* WARTESCHLANGE Punkt 22: nur in „Kunde ist dran" (s. Begründung an `gueltigBis` oben),
              gleicher Icon+Text-Baustein wie die Eckdaten eine Zeile höher — kein neuer Chip-Stil.
              Im Entwurf 40b ist diese Angabe der Anfang der Lagezeile. */}
          {gueltigBis && fristAbgelaufen && (
            /* Abgelaufen: derselbe Warn-Baustein wie „Zahlung fehlgeschlagen" oben (text-error). */
            <span data-testid="frist-abgelaufen" className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] font-semibold text-error">
              <Icon name="schedule" className="shrink-0 text-[14px]" />
              {gueltigBis}
            </span>
          )}
          {gueltigBis && !fristAbgelaufen && (
            <span className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
              <Icon name="schedule" className="shrink-0 text-[14px] text-on-surface-variant/70" />
              {gueltigBis}
            </span>
          )}
          {/* WARTESCHLANGE Punkt 25: derselbe Icon+Text-Baustein wie „Gültig bis" daneben, kein
              neuer Chip-Stil — Reihenfolge nach `gueltigBis`, `flex-wrap` auf der Zeile fängt beide
              gleichzeitig ab, falls der Platz eng wird. */}
          {erinnerungAngestossen && (
            <span className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
              <Icon name="notifications_active" className="shrink-0 text-[14px] text-on-surface-variant/70" />
              {erinnerungAngestossen}
            </span>
          )}
          {/* Punkt 79, Zusatz 3: derselbe Icon+Text-Baustein wie die beiden daneben, kein neuer
              Chip-Stil. „Nie geöffnet" ist die Auskunft, die zum Nachfassen führt — sie steht
              deshalb NEBEN der Gültigkeit und nicht in einer eigenen Zeile. Der Entwurf 40b hält
              es genauso: dort steht „Gültig bis 18.09.2026 · 0× geöffnet" nebeneinander in der
              Lagezeile. */}
          {aufrufe && (
            <span
              data-testid="karte-aufrufe"
              className="flex items-center gap-1 whitespace-nowrap font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim"
            >
              <Icon name="visibility" className="shrink-0 text-[14px] text-on-surface-variant/70" />
              {aufrufe}
              {anfrage.tatsachen?.letztesGeraet && anfrage.tatsachen.letztesGeraet !== "unbekannt" && (
                <>
                  <span aria-hidden="true">·</span>
                  <Icon name={geraetIcon(anfrage.tatsachen.letztesGeraet)} className="shrink-0 text-[14px] text-on-surface-variant/70" />
                  {geraetLabel(anfrage.tatsachen.letztesGeraet)}
                </>
              )}
            </span>
          )}
        </div>
      )}

      {/* Notiz-Signal (Antoine, 25.10.2026, Mockup Variante A): nur bei vorhandenen Notizen. */}
      <NotizZeile anfrage={anfrage} />

      {/*
        Zeile 4, Entwurf 40b (21.09.2026): Die „Nächster Schritt"-Leiste steht dort in EINER
        EIGENEN ZEILE über die volle Kartenbreite — ein breiter farbiger Balken, nicht ein Element
        neben den Kurzwegen. Bis hierher teilte sie sich eine Zeile mit den Icon-Knöpfen und war
        entsprechend schmal. Reine Anordnung: derselbe Text, dieselbe Aktion, dieselbe Farbe.
      */}
      <NaechsterSchrittLeiste stand={stand} onKlick={(a) => onAktion(anfrage, a)} />
      {/*
        Zeile 5, Entwurf 40b: der zweite Einstieg („Angebot öffnen") ist dort ein BESCHRIFTETER
        Akzentknopf links, nicht ein stummes Icon; rechts davon stehen die übrigen Kurzwege als
        Icon-Knöpfe und ganz am Ende das „⋯"-Menü. Letzteres saß bisher oben rechts in der
        Kartenecke — im Entwurf sitzt es unten in dieser Zeile.

        KEINE Aktion kommt hinzu und keine fällt weg: Glocke und Zahlungen führen unverändert
        dorthin, wo sie vorher hinführten, das Menü enthält dieselben Einträge. Antoine am
        21.09.2026: „Übernimm aus dem Entwurf keine Funktionen und lass keine weg. Es geht nur ums
        aussehen!!!"
      */}
      <div className="flex items-center gap-2">
        {/*
          Die Glocke steht weiterhin LINKS von „Angebot öffnen" — WARTESCHLANGE Punkt 19, Antoine
          am 14.09.2026 wörtlich: „links von Icon 'Angebot öffnen', ein Icon mit einer Glocke".
          Der erste Versuch dieser Umstellung schob sie nach rechts zu den übrigen Kurzwegen; die
          Prüfung dazu hat das sofort gemeldet. Der Entwurf 40b widerspricht dem nicht: er kennt
          gar keine Glocke, sagt über ihren Platz also nichts.
        */}
        {zeigtErinnerung && erinnerungHref && (
          <KartenIconKnopf
            href={erinnerungHref}
            label="Erinnerung schicken"
            icon="notifications"
            onKlick={() => onErinnerungSchicken(anfrage)}
          />
        )}
        <KartenAkzentLink to={angebotPfad(anfrage.id)} label="Angebot öffnen" icon="description" />
        <span className="flex-1" />
        <KartenIconKnopf to={abrechnungPfad(anfrage.id)} label="Zahlungen" icon="payments" />
        <div className="relative z-[3]" ref={menuRef}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOffen}
            aria-label={menuOffen ? "Weitere Aktionen schließen" : "Weitere Aktionen"}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOffen((v) => !v);
            }}
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-on-surface-variant/60 motion-safe:transition-colors hover:bg-surface-container-high hover:text-on-surface-variant dark:hover:bg-surface-variant/30 dark:hover:text-secondary-fixed-dim ${
              menuOffen ? "bg-surface-container-high text-on-surface-variant dark:bg-white/[0.08]" : ""
            }`}
          >
            <Icon name="more_horiz" className="text-[17px]" />
          </button>
          {menuOffen && (
            <div
              role="menu"
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-9 right-0 z-20 w-52 rounded-xl border border-outline-variant/40 bg-surface-container-lowest py-1.5 shadow-2xl dark:border-outline/30 dark:bg-surface-dim"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => waehleMenuAktion("ansehen_bearbeiten")}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-body-sm text-body-sm text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-surface-variant/20"
              >
                <Icon name={angebotVersendet ? "visibility" : "edit"} className="text-[16px] text-on-surface-variant/70" />
                {angebotVersendet ? "Angebot ansehen" : "Bearbeiten"}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => waehleMenuAktion("notiz")}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-body-sm text-body-sm text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-surface-variant/20"
              >
                <Icon name="note_add" className="text-[16px] text-on-surface-variant/70" />
                Notiz hinzufügen
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => waehleMenuAktion("antworten")}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-body-sm text-body-sm text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-surface-variant/20"
              >
                <Icon name="reply" className="text-[16px] text-on-surface-variant/70" />
                Per E-Mail antworten
              </button>
              <hr className="my-1.5 border-outline-variant/30 dark:border-outline/20" />
              <button
                type="button"
                role="menuitem"
                onClick={() => waehleMenuAktion("absagen")}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-body-sm text-body-sm text-error hover:bg-surface-container-high dark:hover:bg-surface-variant/20"
              >
                <Icon name="event_busy" className="text-[16px]" />
                Absagen
              </button>
              {zeigtSpamOption && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => waehleMenuAktion("spam")}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-body-sm text-body-sm text-error hover:bg-surface-container-high dark:hover:bg-surface-variant/20"
                >
                  <Icon name="report" className="text-[16px]" />
                  Als Spam löschen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function KartenListe({
  zeilen,
  gruppe,
  zeilenZiel,
  onAktion,
  onMenuAktion,
  onErinnerungSchicken,
}: {
  zeilen: AnfrageZeile[];
  gruppe: KanbanSpalte;
  zeilenZiel: (anfrage: Inquiry) => string;
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  onMenuAktion: (anfrage: Inquiry, aktion: KanbanMenuAktion) => void;
  onErinnerungSchicken: (anfrage: Inquiry) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {zeilen.map(({ anfrage, stand }) => (
        <KanbanKarte
          key={anfrage.id}
          anfrage={anfrage}
          stand={stand}
          gruppe={gruppe}
          zeilenZiel={zeilenZiel}
          onAktion={onAktion}
          onMenuAktion={onMenuAktion}
          onErinnerungSchicken={onErinnerungSchicken}
        />
      ))}
    </div>
  );
}

/**
 * Die Abschnitte auf das reduzieren, was auf dem Board steht (`kanbanZeigt`), und den Untertitel
 * („N Anfragen · …") aus genau diesen Zeilen neu zählen — sonst nennte der Kopf eine Anzahl, zu
 * der die Karten darunter nicht passen.
 */
type BoardAbschnitt = Omit<AnzeigeAbschnitt, "gruppe"> & { gruppe: KanbanSpalte };

/**
 * Seit 12.10.2026 zieht sie außerdem die abgelaufenen Karten (`istAbgelaufen`) aus ihren
 * Regel-Spalten heraus in die eigene Spalte „Abgelaufen" direkt nach „Kunde ist dran".
 */
function boardAbschnitte(abschnitte: AnzeigeAbschnitt[], heute: Date = new Date()): BoardAbschnitt[] {
  const abgelaufen: AnfrageZeile[] = [];
  const gefiltert = abschnitte.map((a) => ({
    ...a,
    zeilen: a.zeilen.filter((z) => {
      if (!kanbanZeigt(z.anfrage, heute)) return false;
      if (!istAbgelaufen(z.anfrage, heute)) return true;
      abgelaufen.push(z);
      return false;
    }),
  }));
  const anzahl: Record<StandGruppe, number> = { du_bist_dran: 0, kunde_ist_dran: 0, gebucht: 0, abgelehnt: 0 };
  for (const a of gefiltert) for (const z of a.zeilen) anzahl[z.stand.gruppe] += 1;
  const board: BoardAbschnitt[] = gefiltert.map((a) => ({ ...a, untertitel: gruppenUntertitel(a.gruppe, anzahl) }));
  const n = abgelaufen.length;
  const spalte: BoardAbschnitt = { gruppe: "abgelaufen", titel: "Abgelaufen", untertitel: `${n} ${n === 1 ? "Anfrage" : "Anfragen"} · Frist verstrichen oder keine Antwort`, zeilen: abgelaufen };
  const nachKunde = board.findIndex((a) => a.gruppe === "kunde_ist_dran") + 1;
  board.splice(nachKunde, 0, spalte);
  return board;
}

export function AnfragenKanban({ abschnitte: alle, zeilenZiel, onAktion, onMenuAktion, onErinnerungSchicken }: AnfragenKanbanProps) {
  // DIE eine Stelle (Fix 27.09.2026, s. `kanbanZeigt`): ab hier liest alles — Karten, Summe,
  // Untertitel, Geldzeile — dieselbe gefilterte Menge. Nie `alle` weiter unten verwenden.
  const abschnitte = boardAbschnitte(alle);
  const leer = abschnitte.every((a) => a.zeilen.length === 0);
  if (leer) {
    return (
      <Card className="overflow-hidden">
        {/* Listenstandard (Punkt 95): kompakter, einheitlicher Leerzustand wie in allen Listen. */}
        <EmptyState
          icon="inbox"
          title="Keine Anfragen"
          hint="Sobald über Ihr Anfrageformular oder per E-Mail eine Anfrage eingeht, erscheint sie automatisch hier."
        />
      </Card>
    );
  }

  return (
    // Redesign 08.09.2026, Korrektur 08.09.2026 (Antoine, Mobile-Verhalten geaendert): auf Desktop
    // (ab `md:`, Projekt-Standardbruch wie in `AnfragenGruppen.tsx` GruppenTabelle/GruppenKarten)
    // drei gleich breite Spalten ueber die volle Breite (`md:grid md:grid-cols-3`) — das war nie
    // strittig. Auf Mobile bleibt es dagegen beim URSPRUENGLICHEN Verhalten: Spalten NICHT
    // untereinander gestapelt, sondern nebeneinander mit horizontalem Scroll (feste Kartenbreite
    // `w-[300px] shrink-0`, wie vor diesem Redesign) — kein neuer Media-Query-Wechsel auf
    // Flex-Direction dort. Kein Hoehen-Container pro Spalte (dieselbe Hausregel wie
    // `GruppenKarten`: das Dokument scrollt, nicht ein zweiter, verschachtelter Bereich).
    //
    // Nachschlag 09.09.2026: kein `Card`-Rahmen mehr UM die ganze Spalte (Mockup: `.col` hat
    // weder Hintergrund noch Rand/Schatten — nur die einzelnen Karten darin sind Tiles). Spalten-
    // abstand 20px (`gap-5`, Mockup `.board{gap:20px}`), Kopf+Kartenliste 12px (`gap-3`, Mockup
    // `.col{gap:12px}`).
    <div className="space-y-2.5">
      {/*
        Punkt 79, Zusatz 6: „126.750 € im Board · 2 Anzahlungen überfällig" über den Spalten.
        Der Text entsteht in `pages/inquiries.tsx` (`boardSummeText`) — dort steht auch, warum er
        NICHT in die Seitenüberschrift gehört: die zählt seit P10.6/32a bewusst nichts mehr.
      */}
      <BoardZeile abschnitte={abschnitte} />
      <div className="flex gap-5 overflow-x-auto pb-2 md:grid md:grid-cols-4 md:items-start md:overflow-visible md:pb-0">
      {abschnitte.map(({ gruppe, titel, untertitel, zeilen }) => (
        <div key={gruppe} data-testid={`kanban-spalte-${gruppe}`} className="flex w-[300px] shrink-0 flex-col gap-3 md:w-auto md:shrink">
          <SpaltenKopf
            gruppe={gruppe}
            titel={titel}
            untertitel={untertitel}
            summeCents={spaltenSumme(zeilen)}
            summeText={spaltenSummeText(zeilen)}
            hinweis={spaltenHinweis(gruppe, zeilen)}
          />
          {zeilen.length === 0 ? (
            <p className="rounded-[14px] border border-dashed border-outline-variant/50 px-4 py-6 text-center font-body-sm text-[12px] text-on-surface-variant/60 dark:border-outline/30">
              Keine Anfragen
            </p>
          ) : (
            <KartenListe
              zeilen={zeilen}
              gruppe={gruppe}
              zeilenZiel={zeilenZiel}
              onAktion={onAktion}
              onMenuAktion={onMenuAktion}
              onErinnerungSchicken={onErinnerungSchicken}
            />
          )}
        </div>
      ))}
      </div>
    </div>
  );
}

/** Punkt 79, Zusatz 6: die Geldzeile über dem Board. Nichts zu sagen? Dann keine Zeile. */
function BoardZeile({ abschnitte }: { abschnitte: BoardAbschnitt[] }) {
  // „Abgelaufen" zählt hier bewusst nicht mit: Geld, das ohne Antwort oder nach Fristablauf
  // aussteht, ist nicht „im Board" (Antoine, 21.09.2026: verlorene gehören nicht in die Summe).
  // Ihre eigene Spaltensumme steht weiter im Spaltenkopf.
  const text = boardSummeText(abschnitte.filter((a) => a.gruppe !== "abgelaufen").flatMap((a) => a.zeilen.map((z) => z.anfrage)));
  if (!text) return null;
  return (
    <p data-testid="board-summe" className="px-0.5 font-body-sm text-[12.5px] font-medium text-on-surface-variant">
      {text}
    </p>
  );
}

